import { ShopifyProductsPage, ShopifyStore, ShopifyStoreDataDetailsDto } from 'shared/interfaces/ShopifyStore';
import { Website } from 'shared/interfaces/Website';
import { axiosBaseInstance } from 'shared/utils/axios-instances';

const shopifyBaseEndpoint = '/shopify';
const shopifyStoreIdKey = 'shopifyStoreId';

const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24;
interface ShopifyStoreLocalStorageData {
  storeId: string;
  shopUrl: string;
  expiresAt: number;
}

export const ShopifyService = {
  getAllStores: async (): Promise<ShopifyStore[]> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${shopifyBaseEndpoint}/store/all`
    });

    return response.data;
  },

  getStoreById: async (storeId: string): Promise<ShopifyStore> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${shopifyBaseEndpoint}/store/${storeId}`
    });

    return response.data;
  },

  getStoreWebsite: async (storeId: string): Promise<Website> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${shopifyBaseEndpoint}/store/${storeId}/website`
    });

    return response.data;
  },

  getActiveStoreProductsPage: async (storeId: string, nextPageCursor?: string): Promise<ShopifyProductsPage> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${shopifyBaseEndpoint}/store/${storeId}/products${nextPageCursor ? `/${nextPageCursor}` : ''}`
    });

    return response.data;
  },

  getEmbeddedStoreData: async (storeId: string): Promise<ShopifyStoreDataDetailsDto> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${shopifyBaseEndpoint}/store/${storeId}/embedded-data`
    });

    return response.data;
  },

  getActiveStoreProductsCount: async (storeId: string): Promise<number> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${shopifyBaseEndpoint}/store/${storeId}/product-count`
    });

    return response.data.count;
  },

  linkStore: async (storeId: string): Promise<ShopifyStore> => {
    const response = await axiosBaseInstance({
      method: 'post',
      url: `${shopifyBaseEndpoint}/store/link`,
      data: { id: storeId }
    });

    return response.data;
  },

  setShopifyStoreIdToLocalStorage: (storeId: string, shopUrl?: string): void =>
    localStorage.setItem(
      shopifyStoreIdKey,
      JSON.stringify({
        storeId,
        shopUrl,
        expiresAt: Date.now() + ONE_DAY_IN_MS
      })
    ),

  getShopifyStoreIdFromLocalStorage: (): string | null => {
    const data = localStorage.getItem(shopifyStoreIdKey);

    if (!data) return null;

    const { storeId, expiresAt } = JSON.parse(data) as ShopifyStoreLocalStorageData;

    if (Date.now() > expiresAt) {
      ShopifyService.removeShopifyStoreIdFromLocalStorage();
      return null;
    }

    return storeId;
  },

  getShopifyShopUrlFromLocalStorage: (): string | null => {
    const data = localStorage.getItem(shopifyStoreIdKey);

    if (!data) return null;

    const { shopUrl, expiresAt } = JSON.parse(data) as ShopifyStoreLocalStorageData;

    if (Date.now() > expiresAt) {
      ShopifyService.removeShopifyStoreIdFromLocalStorage();
      return null;
    }

    return shopUrl;
  },

  removeShopifyStoreIdFromLocalStorage: (): void => localStorage.removeItem(shopifyStoreIdKey),

  isShopifyStoreDataInLocalStorage: (): boolean => {
    const data = localStorage.getItem(shopifyStoreIdKey);

    if (!data) return false;

    const { expiresAt } = JSON.parse(data) as ShopifyStoreLocalStorageData;

    if (Date.now() <= expiresAt) return true;

    ShopifyService.removeShopifyStoreIdFromLocalStorage();

    return false;
  }
};
